import { useEffect, useState, useRef } from "react";
import { t } from "i18next";
import { publishViewedSubscriptionOfferEvent } from "../root/api/eventsApi";
import {
  EventContextArea,
  FunnelContextArea,
} from "../../components/funnel/types";
import { useGetNavigationContext } from "../../hooks/useGetNavigationContext";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import {
  useToast,
  Flex,
  Grid,
  GridItem,
  Box,
  Text,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { DownloadOurApp } from "../../components/downloadOurApp/DownloadOurApp";
import { useContentFunnel } from "../../components/funnel/hooks/useContentFunnel";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import { GiftThickIcon } from "../../components/icons/GiftThickIcon";
import { HeartThickIcon } from "../../components/icons/HeartThickIcon";
import { PagesThickIcon } from "../../components/icons/PagesThickIcon";
import { WifiOffThickIcon } from "../../components/icons/WifiOffThickIcon";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { DealElement } from "./components/DealElement";
import { ListOfPopularComics } from "./components/ListOfPopularComics";
import { ListOfFeaturedPublishers } from "./components/ListOfPublishers";
import { ListOfReviews } from "./components/ListOfReviews";
import { useAccountData } from "../../hooks/useAccountData";
import { setFunnelModalConfig } from "../../components/funnel/FunnelModal";
import { skipToken, useQuery } from "@tanstack/react-query";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../components/auth/api/authApi";

export function GoldScreen() {
  const [funnelType, setFunnelType] = useState<FUNNEL_TYPES | undefined>(
    FUNNEL_TYPES.subscription,
  );
  const hasPublishedEvent = useRef(false);
  const accountData = useAccountData();
  const { isLoading: isLoadingAccountData } = useQuery({
    queryKey: GET_ACCOUNT_ME_QUERY_KEY,
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });
  const enableBlackFridayGoldPage = accountData?.features["black_friday_deal"];

  useTitleOnNavigation(t("screens.gold.subscribeToGlobalComixGold"));

  const navigate = useNavigate();
  const createToast = useToast();
  const { funnelBody } = useContentFunnel({
    setFunnelConfig: (funnelConfig) => setFunnelType(funnelConfig?.type),
    onCloseFunnel: () => setFunnelType(undefined),
    openFunnelType: funnelType,
    context: {
      modalContext: FunnelContextArea.gold,
    },
    options: {
      componentProps: {
        horizontal: "true",
      },
      onSuccess: (result) => {
        const shouldShowCongratsModal =
          accountData?.features["congrats_paywall"] &&
          result.options?.purchasedYearly;

        if (shouldShowCongratsModal) {
          // Show congrats modal first when feature flag is enabled
          setFunnelModalConfig({
            type: FUNNEL_TYPES.congrats,
            context: result.context,
            options: {
              ...result.options,
              skipOfferPaywallModal: true,
              onSuccess: () => {
                setFunnelModalConfig();
                navigate("/");
                createToast({
                  status: "success",
                  description: t("screens.gold.goldSubscriptionActivated"),
                  duration: 5000,
                });
              },
            },
          });
        } else {
          // Original behavior when feature flag is off
          navigate("/");
          createToast({
            status: "success",
            description: t("screens.gold.goldSubscriptionActivated"),
            duration: 5000,
          });
        }
      },
    },
  });

  const isSmallBreakpoint = useIsSmallBreakpoint();

  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.goldPage,
  });

  useEffect(() => {
    if (
      !enableBlackFridayGoldPage &&
      accountData &&
      !hasPublishedEvent.current
    ) {
      publishViewedSubscriptionOfferEvent(navigationContext);
      hasPublishedEvent.current = true;
    }
    rootComponentsStore.eventContextReferrer.set(undefined);
  }, [accountData]);

  if (isLoadingAccountData) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <Spinner color="blaze.blaze" size="lg" />
      </Flex>
    );
  }

  return (
    <Flex
      overflow="auto"
      height="100%"
      width="100%"
      position="relative"
      flexDirection="column"
      paddingTop="56px"
      alignItems="center"
      pr={isSmallBreakpoint ? undefined : "72px"}
      pb="50px"
    >
      <Flex
        pl={isSmallBreakpoint ? undefined : "72px"}
        flexDirection={"column"}
        zIndex={1}
        width="100%"
        maxWidth="1200px"
      >
        <Flex width="100%" paddingBottom="40px">
          {funnelBody}
        </Flex>
        <Flex
          flexDirection="column"
          px={isSmallBreakpoint ? "28px" : "0px"}
          gap="48px"
        >
          <Flex flexDirection="column">
            <Text
              textAlign={isSmallBreakpoint ? "center" : "left"}
              variant="goldTitle"
              sx={{
                textWrap: "balance",
              }}
            >
              Discover Your Next Favorite Comic
            </Text>
            <Grid
              paddingTop="40px"
              templateColumns={`repeat(${isSmallBreakpoint ? 1 : 2}, 1fr)`}
              gap={isSmallBreakpoint ? "40px" : "38px"}
            >
              <GridItem>
                <DealElement
                  title="Take your comics anywhere"
                  description="Thousands of comics with a single subscription—on web, tablet, or phone."
                  icon={<PagesThickIcon boxSize="24px" color="blaze.blaze" />}
                />
              </GridItem>
              <GridItem>
                <DealElement
                  title="Read comics offline"
                  description="Download comics in the GlobalComix app and read comics without an internet connection."
                  icon={<WifiOffThickIcon boxSize="24px" color="blaze.blaze" />}
                />
              </GridItem>
              <GridItem>
                <DealElement
                  title="Support comic creators"
                  description="Up to 70% of your membership payment gets distributed among the books you read."
                  icon={<HeartThickIcon boxSize="24px" color="blaze.blaze" />}
                />
              </GridItem>
              <GridItem>
                <DealElement
                  title="Share stories with friends"
                  description="Send six free access codes each year to friends without a Gold Membership."
                  icon={<GiftThickIcon boxSize="24px" color="blaze.blaze" />}
                />
              </GridItem>
            </Grid>
          </Flex>
          <ListOfPopularComics />
          <ListOfFeaturedPublishers />
          <ListOfReviews />
          <Box height="1px" width="100%" bg="transparent.white.20" />
          <DownloadOurApp />
        </Flex>
      </Flex>
      <Box
        position="absolute"
        top={0}
        zIndex={0}
        left={0}
        height="70%"
        width="100%"
        bgImage={`${window.gc.global.assets_url}/static/images/web-ui/goldBackground.jpeg`}
        bgRepeat="no-repeat"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backdropBlur="8px"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          background: "blaze.900",
          opacity: 0.87,
        }}
      >
        <Box
          position="absolute"
          bottom={0}
          height="50%"
          width="100%"
          background="linear-gradient(180deg, rgba(20, 21, 23, 0) 0%, #141517 100%)"
        />
      </Box>
    </Flex>
  );
}
